import { useEffect, useRef } from 'react';
import { Button, Divider, NumberInput, SimpleGrid, Skeleton, Stack } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { IconUpload } from '@tabler/icons-react';
import { type FormularDFormData } from '../../types/reports';
import { ValidatedNumberInput } from '../form/validatedFields';
import { DECIMAL_INPUT_PROPS } from '../form/numberFieldPresets';
import { PreviousValueForField } from '../form/PreviousValueLine';
import { RegisterFieldTab } from '../form/RegisterFieldTab';
import { FormCard } from '../ui/FormCard';
import { FormSubsection } from '../ui/FormSubsection';
import { SummaryBar } from '../ui/SummaryBar';
import { createCalcStore, type ValuesVersionStore } from './formStores';
import { FORMULAR_D_FIELD_KEYS } from './formularD/catalog';
import {
  calculateBeitragspflichtig,
  calculateGrandTotal,
  calculateSectionITotal,
  type FormularDCalculations,
} from './formularD/totals';
import { FormularDTotal } from './formularD/DerivedTotal';
import { FormularDSectionI } from './formularD/FormularDSectionI';
import { FormularDSectionII } from './formularD/FormularDSectionII';

// Re-Export, damit hydrate.ts und reportTypes.test.ts ohne Aenderung weiterlaufen (Plan 23-02
// Task 1) -- die Kataloge selbst liegen jetzt in ./formularD/catalog.ts.
export { FORMULAR_D_FIELD_KEYS };

interface FormularDFormProps {
  form: UseFormReturnType<FormularDFormData>;
  runValidation: () => void;
  /** Treibt die Anzeige-Summen (D-13) -- kein Server-Calculator fuer FormularD. */
  valuesVersionStore: ValuesVersionStore;
  /**
   * Server-Vorschau der Summen (D-26). `USE_SERVER_CALC` (totals.ts) steht seit Plan 23-07 auf
   * `true` -- dieser Wert ist damit der Regelfall, nicht der Ausnahmezweig. Er wird nicht direkt
   * an die Anzeige durchgereicht: `FormularDForm` spiegelt ihn per `useEffect` in einen lokalen
   * `calcStore` (D-05), den `FormularDTotal` feldgenau abonniert. Nur so bleibt die `calc`-
   * Referenz, die bei jeder Server-Antwort neu entsteht, unterhalb der Karten nicht mehr
   * sichtbar (Voraussetzung fuer das `memo()` aus Plan 27-02).
   */
  calc?: FormularDCalculations | null;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * `FormularDForm` ist seit Plan 23-06 reine Schale: drei `FormSubsection`-Bloecke (Abschnitt I,
 * II, III & IV), zwei Summenbalken (D-08) und die Fussnoten. Tabellen-/Kartenlogik liegt in
 * `formularD/FormularDSectionI.tsx` und `formularD/FormularDSectionII.tsx`. Der formularinterne
 * Titel entfaellt (D-06) -- `PageHeader` (ReportEditPage.tsx) traegt ihn bereits; interne
 * Kommentare bleiben im Notizen-Fenster (D-32), kein Inline-Kommentarfeld hier.
 */
export function FormularDForm({ form, runValidation, valuesVersionStore, calc, disabled, loading }: FormularDFormProps) {
  // D-05: der Store entsteht dort, wo seine einzigen Konsumenten sind. Ein Werkswechsel baut die
  // Komponente ueber den Route-Key `type/id` (ReportEditRoute) ohnehin neu auf und erzeugt dabei
  // einen frischen Store -- kein Aufraeumcode noetig.
  const calcStore = useRef(createCalcStore()).current;

  // Die EINZIGE Schreibstelle. Zwingend im Effekt, nicht im Render: `store.set` benachrichtigt
  // Abonnenten synchron, und das waehrend des Renders einer anderen Komponente ist Reacts
  // Warnung "Cannot update a component while rendering a different component". Der Effekt laeuft
  // genau einmal je eintreffender Server-Antwort (`calc`-Identitaet), nicht pro Tastendruck.
  useEffect(() => {
    calcStore.set(calc ?? {});
  }, [calc, calcStore]);

  return (
    <div onBlur={runValidation}>
      {/* Der weisse Inhaltsrahmen der Referenzformulare (Plan 23-09). Im Figma-Screen umschliesst
          er den gesamten Formularinhalt inklusive beider Summenbalken. */}
      <FormCard>
      <Stack>
        {/* Abschnitt I: Normzement -- der deaktivierte CSV-Import-Button sitzt hier im Kopf
            (D-06), unveraendert wirkungslos. */}
        <FormSubsection
          title="I. Normzement DIN EN 197 u. DIN 1164"
          description="Zementartenstatistik nach Festigkeitsklassen"
          rightSection={
            <Button variant="default" leftSection={<IconUpload size={14} />} disabled>
              CSV-Import
            </Button>
          }
        >
          <Stack gap="md">
            <FormularDSectionI
              form={form}
              store={valuesVersionStore}
              calcStore={calcStore}
              disabled={disabled}
              loading={loading}
            />
            <FormularDTotal
              form={form}
              store={valuesVersionStore}
              calcStore={calcStore}
              serverKey="summe_normzement"
              compute={calculateSectionITotal}
            >
              {(sectionITotal) => (
                <>
                  <SummaryBar label="Normzement Gesamt" variant="muted" loading={loading} value={sectionITotal} />
                  <PreviousValueForField field="summe_normzement" />
                </>
              )}
            </FormularDTotal>
          </Stack>
        </FormSubsection>

        <Divider />

        {/* Abschnitt II: Sonstige Bindemittel -- drei Tabellen mit je eigenen
            Spaltenkoepfen (D-04/D-05). */}
        <FormSubsection
          title="II. Sonstige Bindemittel"
          description="Bauaufsichtlich zugelassene Zemente und weitere Bindemittel"
        >
          <FormularDSectionII
            form={form}
            store={valuesVersionStore}
            calcStore={calcStore}
            disabled={disabled}
            loading={loading}
          />
        </FormSubsection>

        <Divider />

        {/* Abschnitt III & IV: ohne Karten, einfaches Grid (D-07). */}
        <FormSubsection
          title="III. & IV. Weitere Angaben"
          description="Klinkermehl und beitragspflichtige Produktmengen"
        >
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            {loading ? (
              <>
                <Skeleton height={62} radius="sm" />
                <Skeleton height={62} radius="sm" />
                <Skeleton height={62} radius="sm" />
              </>
            ) : (
              <>
                <ValidatedNumberInput
                  form={form}
                  field="klinkermehl"
                  kind="decimal"
                  label="III. Klinkermehl"
                  disabled={disabled}
                  suffix=" t"
                  {...DECIMAL_INPUT_PROPS}
                  styles={{ input: { textAlign: 'right', fontWeight: 500, fontVariantNumeric: 'tabular-nums' } }}
                />

                {/* D-20/D-22: gesperrtes Feld, nur deaktiviert, ohne Erklaerzeile oder Hinweis-
                    Overlay beim Ueberfahren. Der Wert kommt server-berechnet (bzw. clientseitig
                    via calculateBeitragspflichtig, solange USE_SERVER_CALC=false) -- der Melder
                    tippt hier nichts mehr ein. */}
                <FormularDTotal
                  form={form}
                  store={valuesVersionStore}
                  calcStore={calcStore}
                  serverKey="beitragspflichtig"
                  compute={calculateBeitragspflichtig}
                >
                  {(beitragspflichtig) => {
                    const parsed = beitragspflichtig != null ? parseFloat(beitragspflichtig) : NaN;
                    return (
                      <div>
                        <NumberInput
                          label="IV. Beitragspflichtige Mengen"
                          value={Number.isNaN(parsed) ? '' : parsed}
                          readOnly
                          disabled
                          suffix=" t"
                          {...DECIMAL_INPUT_PROPS}
                          fixedDecimalScale
                          styles={{ input: { textAlign: 'right', fontWeight: 500, fontVariantNumeric: 'tabular-nums' } }}
                        />
                        {/* Dritter, eigenstaendiger Anzeigetyp (weder ValidatedNumberInput noch
                            SummaryBar) -- der Vorwert kommt aus dem Rechenaufruf aus Task 1 und
                            ueberschreibt den aus der persistierten Spalte, deckungsgleich mit der
                            laufenden Anzeige, die ebenfalls den berechneten Wert zeigt. */}
                        <RegisterFieldTab field="beitragspflichtig" label="IV. Beitragspflichtige Mengen" />
                        <PreviousValueForField field="beitragspflichtig" />
                      </div>
                    );
                  }}
                </FormularDTotal>

                {/* Vergleichswert vom Server (Phase 44, D-01): Jahressumme der Meldungen
                    Inlandsversand des Werks. Kein Formularfeld -- weder getInputProps noch
                    Eintrag in FORMULAR_D_FIELD_KEYS, damit er nie im Payload landet. */}
                <FormularDTotal
                  form={form}
                  store={valuesVersionStore}
                  calcStore={calcStore}
                  serverKey="inlandsversandComparison"
                  compute={() => null}
                >
                  {(comparison) => {
                    const parsed = comparison != null ? parseFloat(comparison) : NaN;
                    return (
                      <NumberInput
                        label="Inlandsversand"
                        value={Number.isNaN(parsed) ? '' : parsed}
                        readOnly
                        disabled
                        suffix=" t"
                        {...DECIMAL_INPUT_PROPS}
                        fixedDecimalScale
                        styles={{ input: { textAlign: 'right', fontWeight: 500, fontVariantNumeric: 'tabular-nums' } }}
                      />
                    );
                  }}
                </FormularDTotal>
              </>
            )}
          </SimpleGrid>
        </FormSubsection>

        {/* Summenbalken (D-08): grau fuer Abschnitt I steht bereits oben; hier der gelbe
            Gesamtsummenbalken ueber alle Zementarten -- ohne Rechner-Icon, ohne
            Doppelueberschrift. */}
        <FormularDTotal
          form={form}
          store={valuesVersionStore}
          calcStore={calcStore}
          serverKey="gesamtmenge"
          compute={calculateGrandTotal}
        >
          {(grandTotal) => (
            <>
              <SummaryBar
                label="Gesamtsumme aller Zementarten"
                variant="highlight"
                loading={loading}
                value={grandTotal}
              />
              <PreviousValueForField field="gesamtmenge" />
            </>
          )}
        </FormularDTotal>

        {/* Kein Fussnotenapparat mehr: die beiden Texte stehen seit der Kartenumstellung als
            Hinweiszeile im Kopf des jeweiligen Blocks in Abschnitt II (`note` in
            formularD/catalog.ts). Hier unten, in 10px Grau hinter dem gelben Summenbalken,
            wurden sie schlicht nicht gelesen. */}
      </Stack>
      </FormCard>
    </div>
  );
}
